// src/components/Notifications.jsx
//
// New page -- the header bell dropdown's "View all" link has pointed at
// /notifications since Header.jsx was written, but the route/page never
// existed until now. Matches CET-HTML-Code/notifications.html: All /
// Read / Unread tabs above a table (Message / Status / Date Received /
// delete action). Real data from GET /notifications (see
// help_and_notifications_tables.sql for the table this reads, and
// app.py's create_notification() for what actually writes rows into it
// -- account activation, password change, and finishing a cost
// estimation, for now).
//
// The theme's own tabs use Bootstrap's JS pill-toggle
// (data-bs-toggle="pill"); this app doesn't load Bootstrap's JS (see the
// note in public/index.html), so which tab is active is plain React
// state instead, driving a fresh fetch with the matching ?status= query
// param -- same "re-use the theme's CSS classes, drive them with our own
// state" pattern as every other tab/dropdown/modal built this way so far.
import React, { useCallback, useEffect, useState } from "react";
import { api as axios, API_ORIGIN } from "../config/apiConfig";

const TABS = [
  { key: "all", label: "All" },
  { key: "read", label: "Read" },
  { key: "unread", label: "Unread" }
];

// This page used to group notifications into category sections ("Cost
// Estimation", "Access Request", ...) via groupNotificationsByCategory +
// NOTIFICATION_CATEGORY_ORDER, same as the header bell dropdown
// (Header.jsx). Per request that grouping was removed on both -- a fixed
// category order meant a brand new notification (e.g. a just-submitted
// Help message) could land in a section near the bottom instead of
// showing at the top, even though it was the most recent one overall.
// The table below renders `notifications` flat now, newest first (the
// API already returns it that way), with n.category only used per-row
// for its icon (see getNotificationIcon) -- no more per-category header
// rows.

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year} – ${hh}:${mm}`;
}

// Date + time, same as the header bell dropdown's formatNotificationDate
// and identical to formatDate() above -- used in the "Name - date" line
// below. Was date-only ("26 Aug 2026"); per request now includes the
// time too, same as the table's own Date Received column.
function formatDateOnly(iso) {
  return formatDate(iso);
}

// Same per-type icon as the header bell dropdown (Header.jsx) -- Cost
// Estimation notifications (Started and Completed) show the actor's real
// avatar instead (see isCostEstimationNotification below), so this only
// covers Access Request / User Management / Help / everything else.
// Per request, Access Request / User Management / the default fallback
// now render the branded icon images from public/assets/images/logo/
// (Access Request.png / New User.png / Defuult User.png -- filenames as
// provided) instead of a plain emoji; Help keeps its emoji since no icon
// was supplied for it.
function getNotificationIcon(n) {
  if (n.category === "Access Request") {
    return <img src={encodeURI("/assets/images/logo/Access Request.png")} alt="Access Request" style={{ width: 28, height: 28 }} />;
  }
  if (n.category === "User Management") {
    return <img src={encodeURI("/assets/images/logo/New User.png")} alt="New User" style={{ width: 28, height: 28 }} />;
  }
  // "Help" -- per request, a Help Center "Send Us a Message" submission
  // (submit_help_message in app.py, fanned out to every Super Admin the
  // same way Access Request/User Management are) shows up here too.
  // Per a later request, this now uses the System Icon.png image instead
  // of the plain speech-bubble emoji.
  if (n.category === "Help") {
    return <img src={encodeURI("/assets/images/logo/System Icon.png")} alt="Help" style={{ width: 28, height: 28 }} />;
  }
  return <img src={encodeURI("/assets/images/logo/Defuult User.png")} alt="User" style={{ width: 28, height: 28 }} />;
}

// True for any Cost Estimation notification (both "Started" and
// "Completed") -- these show the actor's real avatar (NotificationAvatar
// below) instead of an emoji from getNotificationIcon. Same as
// Header.jsx's copy of this function.
function isCostEstimationNotification(n) {
  return n.category === "Cost Estimation";
}

// Same avatar-with-initials-fallback as the header bell dropdown
// (Header.jsx) -- see its copy of this component for the full reasoning.
// Was 20px, then 50% larger (30px); per request now another 30% larger
// on top of that -- 39px, with the initials-fallback font size scaled up
// to match (15 -> 20) so the initial still looks centered/proportional in
// the bigger circle.
function NotificationAvatar({ actorName, actorProfileImage }) {
  const [imgError, setImgError] = useState(false);
  const initial = (actorName || "?").trim().charAt(0).toUpperCase() || "?";
  if (actorProfileImage && !imgError) {
    return (
      <img
        src={`${API_ORIGIN}/profile_photo/${actorProfileImage}`}
        alt={actorName || "User"}
        className="rounded-circle"
        style={{ width: 39, height: 39, objectFit: "cover", verticalAlign: "middle" }}
        onError={() => setImgError(true)}
      />
    );
  }
  return (
    <span
      className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center fw-semibold"
      style={{ width: 39, height: 39, fontSize: 20, verticalAlign: "middle" }}
    >
      {initial}
    </span>
  );
}

export default function Notifications() {
  const [activeTab, setActiveTab] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [marking, setMarking] = useState(false);

  const load = useCallback(async (tab) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get("/notifications", { params: { status: tab } });
      setNotifications(res.data?.notifications || []);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load notifications.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(activeTab);
  }, [activeTab, load]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/notifications/${id}`);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || "Could not delete this notification.");
    }
  };

  const handleMarkAllRead = async () => {
    setMarking(true);
    try {
      await axios.put("/notifications/read-all");
      load(activeTab);
    } catch (err) {
      alert(err.response?.data?.message || "Could not mark notifications as read.");
    } finally {
      setMarking(false);
    }
  };

  return (
    <>
      <div className="align-items-center mb-3 d-flex page-title-head">
        <div className="flex-grow-1">
          {/* Per request, this is now a breadcrumb trail ("Cost
              Estimation / {page}"), same pattern and color (#02bc9c,
              16px) as the Cost Estimation wizard's Step1-7 breadcrumbs
              -- replaces the earlier #788CA5 bordered/uppercase title
              treatment. */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              Notifications
            </span>
          </nav>
        </div>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleMarkAllRead} disabled={marking}>
          {marking ? "Marking..." : "Mark all as read"}
        </button>
      </div>

      <div className="align-items-center mb-3 row">
        <div className="d-flex col-md-6 justify-content-start">
          <ul className="bg-light border flex-nowrap flex-sm-wrap nav nav-pills overflow-auto p-1 rounded">
            {TABS.map((tab) => (
              <li className="flex-shrink-0 nav-item" key={tab.key}>
                <button
                  type="button"
                  className={`px-3 fw-semibold nav-link px-md-4${activeTab === tab.key ? " active" : ""}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-0 card shadow-sm">
        <div className="table-responsive">
          {/* notifications-table (new, scoped) -- per request, same
              treatment as RateCard.jsx/ProjectList.jsx/UsersList.jsx/
              EmailConfiguration.jsx/ManageRoles.jsx: row background
              #f2f2f2 @ 38% Transparency, hover #02bc9c @ 95%
              Transparency, via themeOverrides.css. Its own class rather
              than changing .table-custom/.table-hover directly, since
              those are shared elsewhere and shouldn't change color
              there too. */}
          {/* FIX: the table used to be `text-nowrap` on every cell (same
              bug RateCard.jsx's table had -- see its own fix comment),
              which forced everything onto one line and made the whole
              table wider than its card, pushing a horizontal scrollbar
              that scrolled the ENTIRE page (sidebar/header included) --
              not just the table -- leaving a blank gap on the left once
              scrolled right. Dropped table-wide text-nowrap; the columns
              that should still stay on one line (Status, Date Received,
              Action) get it individually below, while Message wraps. */}
          <table className="align-middle mb-0 table table-centered table-hover notifications-table w-100">
            <thead>
              <tr className="border-bottom">
                {/* paddingLeft: 28 = the row's avatar width (20px) + its
                    gap (8px) -- per request, this lines "Message" (and
                    "COST ESTIMATION" below it) up with where the actual
                    name/message text starts in each row, since that text
                    sits to the right of the avatar rather than at the
                    cell's own left edge. */}
                <th className="fw-bold py-3" style={{ width: "50%", paddingLeft: 28 }}>
                  Message
                </th>
                <th className="text-center fw-bold py-3 text-nowrap" style={{ width: "15%" }}>
                  Status
                </th>
                <th className="text-center fw-bold py-3 text-nowrap" style={{ width: "20%" }}>
                  Date Received
                </th>
                <th className="text-center fw-bold py-3 text-nowrap" style={{ width: "15%" }}>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="text-center py-4 text-muted">
                    Loading...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={4} className="text-center py-4 text-danger">
                    {error}
                  </td>
                </tr>
              ) : notifications.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-4 text-muted">
                    {activeTab === "all" ? "No notifications yet." : `No ${activeTab} notifications.`}
                  </td>
                </tr>
              ) : (
                // Per request, the category header rows ("COST
                // ESTIMATION" etc.) were removed -- this used to be
                // groupNotificationsByCategory(notifications).map(...)
                // with a full-width label row ahead of each category's
                // own rows; now it's just one flat list of every
                // notification, newest first (the API already returns it
                // that way), same as the header bell dropdown.
                notifications.map((n) => (
                      <tr key={n.id}>
                        <td className="text-wrap" style={{ paddingLeft: 0, textAlign: "left" }}>
                          {/* actorName is only present for rows written
                              after notifications_actor_name_column.sql --
                              an older row just shows its plain message
                              with no name line above it. Full date/time
                              already has its own column to the right, so
                              it isn't repeated here (unlike the bell
                              dropdown, which has no separate date
                              column). */}
                          {/* Per request: avatar sits to the left,
                              vertically centered against BOTH lines of
                              text ("Name - date" on top, message below)
                              rather than only inline with the message's
                              own line. align-items-center on this flex
                              row is what centers the avatar against the
                              full height of the two stacked lines. */}
                          {/* paddingLeft: 0 + textAlign: "left" on the
                              cell, and width: "100%" + justifyContent:
                              "flex-start" on the flex row itself -- belt
                              and braces against whatever theme rule was
                              still pushing this content inward even with
                              the cell's own padding removed (the earlier
                              paddingLeft: 0 alone didn't close the gap,
                              so this pins the flex row's own box to the
                              cell's full width and forces its children
                              to the actual start of that box rather than
                              trusting the default alignment). */}
                          <div
                            className="d-flex align-items-center gap-2"
                            style={{ width: "100%", justifyContent: "flex-start", textAlign: "left" }}
                          >
                            {isCostEstimationNotification(n) ? (
                              <NotificationAvatar actorName={n.actorName} actorProfileImage={n.actorProfileImage} />
                            ) : (
                              // Sized to match NotificationAvatar's 39px
                              // box above -- per request, Access Request /
                              // User Management notifications' emoji icon
                              // should be as big as the Cost Estimation
                              // avatar, not the smaller original 20px.
                              <span className="flex-shrink-0" style={{ width: 39, fontSize: 26, textAlign: "center" }}>
                                {getNotificationIcon(n)}
                              </span>
                            )}
                            <div>
                              {n.actorName && (
                                <div className="small text-muted">
                                  {n.actorName} - {formatDateOnly(n.createdAt)}
                                </div>
                              )}
                              <div>{n.message}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-center text-nowrap">
                          <span
                            className={`badge px-3 py-2 ${
                              n.status === "Read" ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"
                            }`}
                          >
                            {n.status}
                          </span>
                        </td>
                        <td className="text-center text-nowrap">{formatDate(n.createdAt)}</td>
                        <td className="text-center text-nowrap">
                          <button
                            type="button"
                            className="btn btn-icon btn-outline-secondary btn-sm"
                            onClick={() => handleDelete(n.id)}
                            title="Delete"
                          >
                            <i className="ti ti-trash" />
                          </button>
                        </td>
                      </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && !error && notifications.length > 0 && (
          <div className="border-0 bg-transparent card-footer py-3 text-end text-muted">
            Showing {notifications.length} of {notifications.length} entries
          </div>
        )}
      </div>
    </>
  );
}
